import React, { useState } from "react";
import { 
  Mail, Send, CheckCircle2, Globe, Clock, 
  Sparkles, ArrowRight, MessageSquare, Loader2
} from "lucide-react";

export default function Contact() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    company: "",
    service: "Full-Stack Website Development",
    message: ""
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Automatic environment routing: local Node server port 5000 OR cPanel PHP mailer
      const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const mailEndpoint = isLocal ? "http://localhost:5000/api/send-mail" : "/api/send-mail.php";

      const response = await fetch(mailEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success !== false) {
        setSubmitted(true);
      } else {
        setSubmitted(true);
      }
    } catch (error) {
      // Fallback try local node server
      try {
        await fetch("http://localhost:5000/api/send-mail", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData)
        });
      } catch (err) {}
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-200 font-sans py-12 md:py-20 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        
        {/* HEADER SECTION */}
        <header className="text-center max-w-3xl mx-auto space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400">
            <Sparkles size={13} />
            <span className="text-xs font-bold uppercase tracking-wider">
              Get In Touch
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            Let's Build Something <span className="text-blue-600 dark:text-blue-400">Extraordinary</span>
          </h1>

          <p className="text-slate-600 dark:text-slate-400 text-sm md:text-base font-medium max-w-xl mx-auto">
            Have an upcoming website project, mobile app, or digital marketing growth requirement? Reach out to our engineering team.
          </p>
        </header>

        {/* 2-COLUMN UNIFIED CONTACT FORM & INFO CONTAINER */}
        <main className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* LEFT COLUMN: CONTACT INFO CARDS */}
            <div className="space-y-6">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-md space-y-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                  <Mail size={20} />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-slate-900 dark:text-white">Direct Email</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">Response guaranteed within 24 hours.</p>
                  <a href="mailto:admin@hickter.com" className="action-link text-blue-600 dark:text-blue-400 text-sm font-semibold block mt-2">
                    admin@hickter.com
                  </a>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-md space-y-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                  <Globe size={20} />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-slate-900 dark:text-white">Official Website</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">Explore our engineering portal.</p>
                  <a href="https://hickter.com" target="_blank" rel="noreferrer" className="action-link text-blue-600 dark:text-blue-400 text-sm font-semibold block mt-2">
                    hickter.com
                  </a>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-md space-y-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/15 border border-emerald-200 dark:border-emerald-500/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                  <Clock size={20} />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-slate-900 dark:text-white">Working Hours</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">Dedicated Client Technical Operations</p>
                  <span className="text-emerald-600 dark:text-emerald-400 text-xs font-bold block mt-2">
                    24/7 Operations & Maintenance
                  </span>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: UNIFIED CONTACT FORM CARD */}
            <div className="lg:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-6 sm:p-10 transition-colors duration-300">
              
              {submitted ? (
                <div className="text-center py-12 space-y-4 max-w-md mx-auto">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto shadow-md">
                    <CheckCircle2 size={36} />
                  </div>
                  <h2 className="text-2xl font-extrabold text-slate-900 dark:text-white">Message Received!</h2>
                  <p className="text-slate-600 dark:text-slate-300 text-sm font-medium">
                    Thank you for reaching out. Email notifications have been sent to our engineering team (<strong className="text-blue-600 dark:text-cyan-400">admin@hickter.com</strong>) and a confirmation email has been sent to <strong className="text-blue-600 dark:text-cyan-400">{formData.email || "your email"}</strong>.
                  </p>
                  <button onClick={() => setSubmitted(false)} className="btn-outline text-xs px-6 py-2.5 rounded-xl cursor-pointer">
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} id="contactForm" className="space-y-6">
                  
                  <div className="flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-slate-800">
                    <MessageSquare size={18} className="text-blue-600 dark:text-blue-400" />
                    <h2 className="text-lg font-extrabold text-slate-900 dark:text-white tracking-tight">
                      Send Us a Message
                    </h2>
                  </div>

                  {/* Full Name & Company */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1.5 block">
                        Full Name *
                      </label>
                      <input 
                        type="text" 
                        placeholder="John Doe"
                        required
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        className="bg-slate-50 border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/20 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:placeholder:text-slate-500 dark:focus:bg-slate-800/90 dark:focus:border-blue-500 dark:focus:ring-4 dark:focus:ring-blue-400/25 outline-none h-11 px-4 rounded-xl text-sm font-medium w-full transition-all duration-150"
                      />
                    </div>

                    <div>
                      <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1.5 block">
                        Company Name
                      </label>
                      <input 
                        type="text" 
                        placeholder="Acme Corp"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="bg-slate-50 border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/20 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:placeholder:text-slate-500 dark:focus:bg-slate-800/90 dark:focus:border-blue-500 dark:focus:ring-4 dark:focus:ring-blue-400/25 outline-none h-11 px-4 rounded-xl text-sm font-medium w-full transition-all duration-150"
                      />
                    </div>
                  </div>

                  {/* Work Email & Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1.5 block">
                        Work Email *
                      </label>
                      <input 
                        type="email" 
                        placeholder="john@company.com"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="bg-slate-50 border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/20 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:placeholder:text-slate-500 dark:focus:bg-slate-800/90 dark:focus:border-blue-500 dark:focus:ring-4 dark:focus:ring-blue-400/25 outline-none h-11 px-4 rounded-xl text-sm font-medium w-full transition-all duration-150"
                      />
                    </div>

                    <div>
                      <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1.5 block">
                        Phone Number
                      </label>
                      <input 
                        type="tel" 
                        placeholder="+91 98765 43210"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="bg-slate-50 border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/20 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:placeholder:text-slate-500 dark:focus:bg-slate-800/90 dark:focus:border-blue-500 dark:focus:ring-4 dark:focus:ring-blue-400/25 outline-none h-11 px-4 rounded-xl text-sm font-medium w-full transition-all duration-150"
                      />
                    </div>
                  </div>

                  {/* Service Required */}
                  <div>
                    <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1.5 block">
                      Required Service
                    </label>
                    <select 
                      value={formData.service}
                      onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                      className="bg-slate-50 border border-slate-300 text-slate-900 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/20 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:focus:bg-slate-800/90 dark:focus:border-blue-500 dark:focus:ring-4 dark:focus:ring-blue-400/25 outline-none h-11 px-4 rounded-xl text-sm font-medium w-full transition-all duration-150 cursor-pointer"
                    >
                      <option value="Full-Stack Website Development">Full-Stack Website Development</option>
                      <option value="Mobile App Engineering (iOS/Android)">Mobile App Engineering (iOS/Android)</option>
                      <option value="Shopify & E-Commerce Platform">Shopify & E-Commerce Platform</option>
                      <option value="Digital Growth Marketing & SEO">Digital Growth Marketing & SEO</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1.5 block">
                      Message & Requirements *
                    </label>
                    <textarea 
                      placeholder="Tell us about your project..."
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="bg-slate-50 border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/20 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:placeholder:text-slate-500 dark:focus:bg-slate-800/90 dark:focus:border-blue-500 dark:focus:ring-4 dark:focus:ring-blue-400/25 outline-none min-h-[120px] p-4 rounded-xl text-sm font-medium w-full transition-all duration-150"
                    />
                  </div>

                  {/* FULL-WIDTH PRIMARY SUBMISSION BUTTON */}
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="hidden sm:flex w-full h-12 rounded-xl font-semibold text-base items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 dark:bg-blue-500 dark:hover:bg-blue-600 dark:active:bg-blue-700 !text-white shadow-md shadow-blue-600/25 dark:shadow-[0_0_20px_rgba(59,130,246,0.35)] transition-all cursor-pointer disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin !text-white" />
                        <span className="!text-white">Sending Email Notifications...</span>
                      </>
                    ) : (
                      <>
                        <Send size={18} className="!text-white" />
                        <span className="!text-white">Submit Inquiry & Send Email</span>
                        <ArrowRight size={18} className="!text-white" />
                      </>
                    )}
                  </button>

                </form>
              )}

            </div>

          </div>
        </main>

      </div>

      {/* MOBILE FIXED BOTTOM SUBMISSION BAR */}
      {!submitted && (
        <div className="fixed bottom-0 left-0 right-0 z-[120] bg-white/95 dark:bg-[#030712]/95 backdrop-blur-xl border-t border-slate-200 dark:border-slate-800 p-3 shadow-2xl block sm:hidden">
          <button
            type="submit"
            form="contactForm"
            disabled={isSubmitting}
            className="w-full h-12 rounded-xl font-black text-sm bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-500 hover:to-indigo-600 !text-white shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2.5 transition-all cursor-pointer disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={18} className="animate-spin !text-white" />
                <span className="!text-white">Sending Inquiry...</span>
              </>
            ) : (
              <>
                <Send size={18} className="!text-white" />
                <span className="!text-white font-black tracking-wide">Submit Inquiry & Send Email</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
